import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLazyQuery, useMutation } from "@apollo/client";
import "./timesheetPage.css";
import BdrSelectPopup from "../../../../components/BdrInput/BdrSelectPopup";
import {
  GET_CURRENT_USER,
  GET_ALL_USERS,
  GET_USER_TIME_ENTRIES,
} from "../../../../utils/GraphQL/queries";
import { DELETE_TIME_ENTRY } from "../../../../utils/GraphQL/mutations";
import { priceStrFormatter } from "../../../../utils/formatters/priceStrFormatter";
import { useSnackbar } from "../../../../utils/GlobalSnackbar/GlobalSnackbarContext";
import { useConfirm } from "../../../../utils/GlobalConfirm/GlobalConfirmContext";
import { useSSEListener } from "../../../../utils/SSE/SSEContext";
import { hasRole, hasAnyRole } from "../../../../utils/permissions";
import EditTimeEntryModal from "../../adminComponents/EditTimeEntryModal/EditTimeEntryModal";

// ─────────────────────────────────────────────────────────────────────────
// CHANGEME-PAY-PERIOD
// Pay period is Thursday 12:00 AM → Wednesday 11:59 PM (7 days). Mirrors
// server/utils/payCalc.js and PayrollReportPage.jsx. To change, update all
// four constants in lockstep — see payCalc.js for the full list.
// ─────────────────────────────────────────────────────────────────────────
const PAY_PERIOD_START_DAY = 4; // 0=Sun, 1=Mon, ... 4=Thu

function getPayPeriodBounds(referenceDate) {
  const d = new Date(referenceDate);
  const dayOfWeek = d.getDay();
  const daysBack = (dayOfWeek - PAY_PERIOD_START_DAY + 7) % 7;
  const start = new Date(d);
  start.setDate(start.getDate() - daysBack);
  start.setHours(0, 0, 0, 0);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return { start, end };
}

// "Thu Apr 24"
function shortDayLabel(date) {
  return date.toLocaleDateString("en-US", {
    weekday: "short",
    month: "short",
    day: "numeric",
  });
}

// "12:34 PM"
function timeOfDay(ms) {
  if (ms == null) return "—";
  return new Date(parseInt(ms, 10)).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
  });
}

// 7,830,000 ms → "2h 10m"
function formatDuration(ms) {
  if (!ms || ms < 0) return "0m";
  const totalMin = Math.floor(ms / 60000);
  const hr = Math.floor(totalMin / 60);
  const mn = totalMin % 60;
  return hr > 0 ? `${hr}h ${mn}m` : `${mn}m`;
}

// Group an array of entries by their day-of-week label within the period.
function groupByDay(entries, periodStart) {
  const days = [];
  for (let i = 0; i < 7; i += 1) {
    const day = new Date(periodStart);
    day.setDate(day.getDate() + i);
    days.push({ date: day, entries: [] });
  }
  // Match on the LOCAL calendar day, not on elapsed time (2026-09-20).
  // `Math.floor((clockInMs - periodStart) / 24h)` assumes every day is 24
  // hours: in the week containing the fall-back, a clock-in from 23:00 onward
  // lands in the NEXT day's column, and a late entry on the final Wednesday
  // computes idx === 7 and disappears from the grid entirely. The `days`
  // array above is already built with `setDate`, which is DST-safe — only
  // this lookup was not.
  const dayKey = (d) => `${d.getFullYear()}-${d.getMonth()}-${d.getDate()}`;
  const idxByDay = new Map(days.map((d, i) => [dayKey(d.date), i]));
  entries.forEach((e) => {
    if (!e?.clockInMs) return;
    const ms = parseInt(e.clockInMs, 10);
    if (Number.isNaN(ms)) return;
    const idx = idxByDay.get(dayKey(new Date(ms)));
    if (idx !== undefined) days[idx].entries.push(e);
  });
  return days;
}

// Pay (cents) = (totalMs / hour) × hourlyWageCents, rounded to nearest cent.
// Open shifts (clockOutMs == null) are excluded from totals.
function summarize(entries, hourlyWageCents) {
  let totalMs = 0;
  let openCount = 0;
  let openMs = 0;
  entries.forEach((e) => {
    const inMs = parseInt(e.clockInMs, 10);
    if (Number.isNaN(inMs)) return;
    if (e.clockOutMs == null) {
      openCount += 1;
      openMs = Date.now() - inMs;
      return;
    }
    const outMs = parseInt(e.clockOutMs, 10);
    if (Number.isNaN(outMs) || outMs <= inMs) return;
    totalMs += outMs - inMs;
  });
  const wageCents = parseInt(hourlyWageCents || "0", 10);
  const payCents = Math.round((totalMs / 3600000) * wageCents);
  return {
    totalMs,
    openCount,
    openMs,
    payCents: payCents.toString(),
  };
}

const TimesheetPage = () => {
  const { handleSnack } = useSnackbar();
  const { confirm } = useConfirm();
  const [currentUser, setCurrentUser] = useState(null);
  const [allUsers, setAllUsers] = useState([]);
  const [selectedUserId, setSelectedUserId] = useState(null);
  const [periodStart, setPeriodStart] = useState(() => getPayPeriodBounds(new Date()).start);
  const [entries, setEntries] = useState([]);
  // 2026-05-12 — refactored to use the shared EditTimeEntryModal. State
  // shape: null (closed) or { mode: "create"|"edit", initial?: {...} }.
  const [modalState, setModalState] = useState(null);

  const isAdmin = hasRole(currentUser?.permission, "admin");
  const isOfficeOrAdmin = hasAnyRole(currentUser?.permission, ["admin", "office"]);

  const [fetchCurrent] = useLazyQuery(GET_CURRENT_USER, {
    fetchPolicy: "network-only",
  });
  const [fetchAllUsers] = useLazyQuery(GET_ALL_USERS, {
    fetchPolicy: "network-only",
  });
  const [fetchEntries] = useLazyQuery(GET_USER_TIME_ENTRIES, {
    fetchPolicy: "network-only",
  });
  const [deleteMutation] = useMutation(DELETE_TIME_ENTRY);

  // Initial load: current user + (admin only) full user list for picker.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await fetchCurrent();
      if (cancelled) return;
      const u = data?.getCurrentUser;
      if (!u) return;
      setCurrentUser(u);
      setSelectedUserId(u._id);
      if (hasRole(u.permission, "admin")) {
        const { data: usersData } = await fetchAllUsers();
        if (cancelled) return;
        setAllUsers(usersData?.getAllUsers || []);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Load entries whenever selectedUser or period changes.
  useEffect(() => {
    if (!selectedUserId) return;
    let cancelled = false;
    (async () => {
      const periodEnd = new Date(periodStart);
      periodEnd.setDate(periodEnd.getDate() + 6);
      periodEnd.setHours(23, 59, 59, 999);
      const { data } = await fetchEntries({
        variables: {
          userId: selectedUserId,
          periodStartMs: periodStart.getTime().toString(),
          periodEndMs: periodEnd.getTime().toString(),
        },
      });
      if (cancelled) return;
      setEntries(data?.getUserTimeEntries || []);
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedUserId, periodStart]);

  // Resolve which user's wage drives the pay calc.
  const selectedUser = useMemo(() => {
    if (!selectedUserId) return null;
    if (currentUser?._id === selectedUserId) return currentUser;
    return allUsers.find((u) => u._id === selectedUserId) || null;
  }, [selectedUserId, currentUser, allUsers]);

  const summary = useMemo(
    () => summarize(entries, selectedUser?.hourlyWage),
    [entries, selectedUser]
  );

  const grouped = useMemo(() => groupByDay(entries, periodStart), [entries, periodStart]);

  const periodLabel = useMemo(() => {
    const end = new Date(periodStart);
    end.setDate(end.getDate() + 6);
    const opts = { month: "short", day: "numeric" };
    return `${periodStart.toLocaleDateString("en-US", opts)} – ${end.toLocaleDateString("en-US", opts)}`;
  }, [periodStart]);

  const stepPeriod = (deltaWeeks) => {
    const next = new Date(periodStart);
    next.setDate(next.getDate() + deltaWeeks * 7);
    setPeriodStart(next);
  };

  const officePickerUsers = useMemo(
    () => allUsers.filter((u) => hasAnyRole(u.permission, ["admin", "office"])),
    [allUsers]
  );

  const reloadEntries = async () => {
    const periodEnd = new Date(periodStart);
    periodEnd.setDate(periodEnd.getDate() + 6);
    periodEnd.setHours(23, 59, 59, 999);
    const { data } = await fetchEntries({
      variables: {
        userId: selectedUserId,
        periodStartMs: periodStart.getTime().toString(),
        periodEndMs: periodEnd.getTime().toString(),
      },
    });
    setEntries(data?.getUserTimeEntries || []);
  };

  // Cross-tab live refresh. Fires when any user clocks in / out anywhere
  // (server-side SSE always self-delivers for `time_entry_update`). We
  // refetch the displayed user's entries whenever the event's
  // originUserId matches selectedUserId — which means the office user
  // sees their own clock action reflected here, and an admin watching
  // the office user's page sees it the same way.
  const handleTimeEntrySSE = useCallback(
    (data) => {
      if (!selectedUserId) return;
      if (data?.originUserId !== selectedUserId) return;
      reloadEntries();
    },
    // reloadEntries closes over selectedUserId + periodStart so we depend
    // on those directly rather than the callback identity.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectedUserId, periodStart]
  );

  useSSEListener("time_entry_update", handleTimeEntrySSE);

  // 2026-05-12 — Save/edit goes through EditTimeEntryModal, which calls the
  // mutations itself. We just need to reload entries on its onSaved.
  const handleModalSaved = useCallback(
    ({ entry }) => {
      handleSnack(
        modalState?.mode === "edit" ? "Entry updated" : "Entry added",
        2000
      );
      // Modal closes itself on success. Reload to pick up the new/edited row.
      reloadEntries();
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [modalState?.mode]
  );

  const handleDelete = async (entryId) => {
    const ok = await confirm({
      title: "Delete entry?",
      message: "This will remove the time entry. This cannot be undone.",
      confirmLabel: "Delete",
      destructive: true,
    });
    if (!ok) return;
    try {
      const { data } = await deleteMutation({ variables: { entryId } });
      if (data?.deleteTimeEntry) {
        handleSnack("Entry deleted", 2000);
        await reloadEntries();
      }
    } catch (err) {
      handleSnack(err?.message || "Delete failed", 4000);
    }
  };

  if (currentUser && !isOfficeOrAdmin) {
    return (
      <div className="ts-page">
        <div className="ts-container bdr-grad-container-med bdr-border-std bdr-shadow-depth">
          <h1 className="ts-title">Timesheet</h1>
          <p className="ts-empty">Hourly timesheets are admin/office only.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="ts-page">
      <div className="ts-container bdr-grad-container-med bdr-border-std bdr-shadow-depth bdr-dark-placeholder">
        <div className="ts-header">
          <h1 className="ts-title">Timesheet</h1>
          {isAdmin && officePickerUsers.length > 1 && (
            <BdrSelectPopup
              id="ts-user-picker"
              className="bdr-input bdr-select-trigger ts-user-picker"
              value={selectedUserId || ""}
              onChange={(e) => setSelectedUserId(e.target.value)}
              options={officePickerUsers.map((u) => ({
                value: u._id,
                label: `${u.firstName} ${u.lastName || ""}`.trim(),
              }))}
            />
          )}
        </div>

        {/* Period nav */}
        <div className="ts-period-nav">
          <button className="ts-nav-btn" onClick={() => stepPeriod(-1)}>← Prev</button>
          <span className="ts-period-label">{periodLabel}</span>
          <button className="ts-nav-btn" onClick={() => stepPeriod(1)}>Next →</button>
        </div>

        {/* Summary cards */}
        <div className="ts-summary-row">
          <div className="ts-summary-card bdr-border-std">
            <span className="ts-summary-label">Total Hours</span>
            <span className="ts-summary-value">{formatDuration(summary.totalMs)}</span>
          </div>
          <div className="ts-summary-card bdr-border-std">
            <span className="ts-summary-label">Estimated Pay</span>
            <span className="ts-summary-value ts-amount-text">
              {priceStrFormatter(summary.payCents)}
            </span>
          </div>
          <div className="ts-summary-card bdr-border-std">
            <span className="ts-summary-label">Open Shift</span>
            <span className="ts-summary-value">
              {summary.openCount > 0 ? formatDuration(summary.openMs) : "—"}
            </span>
          </div>
          <div className="ts-summary-card bdr-border-std">
            <span className="ts-summary-label">Entries</span>
            <span className="ts-summary-value">{entries.length}</span>
          </div>
        </div>

        {/* Admin-only manual-add */}
        {isAdmin && (
          <div className="ts-actions-row">
            <button
              className="ts-btn ts-btn-primary"
              onClick={() => setModalState({ mode: "create" })}
            >
              + Add Manual Entry
            </button>
          </div>
        )}

        {/* Day groups */}
        <div className="ts-day-list">
          {grouped.map(({ date, entries: dayEntries }) => (
            <div key={date.toISOString()} className="ts-day-group">
              <div className="ts-day-header">
                <span className="ts-day-name">{shortDayLabel(date)}</span>
                <span className="ts-day-count">
                  {dayEntries.length === 0
                    ? "no entries"
                    : `${dayEntries.length} entr${dayEntries.length === 1 ? "y" : "ies"}`}
                </span>
              </div>
              {dayEntries.length > 0 && (
                <div className="ts-entry-list">
                  {dayEntries.map((entry) => {
                    const inMs = parseInt(entry.clockInMs, 10);
                    const isOpen = entry.clockOutMs == null;
                    const durMs = isOpen
                      ? Date.now() - inMs
                      : parseInt(entry.clockOutMs, 10) - inMs;
                    return (
                      <div
                        key={entry.id}
                        className={`ts-entry-row ${isOpen ? "ts-entry-open" : ""}`}
                      >
                        <span className="ts-entry-time">
                          {timeOfDay(entry.clockInMs)} → {isOpen ? "open" : timeOfDay(entry.clockOutMs)}
                        </span>
                        <span className="ts-entry-duration">{formatDuration(durMs)}</span>
                        <span className="ts-entry-source">
                          {entry.source === "manual" ? "manual" : ""}
                          {entry.editedAt ? " · edited" : ""}
                        </span>
                        {isAdmin && (
                          <span className="ts-entry-actions">
                            <button
                              className="ts-icon-btn"
                              onClick={() =>
                                setModalState({
                                  mode: "edit",
                                  initial: {
                                    entryId: entry.id,
                                    clockInMs: entry.clockInMs,
                                    clockOutMs: entry.clockOutMs,
                                    source: entry.source,
                                  },
                                })
                              }
                              title="Edit"
                            >
                              ✎
                            </button>
                            <button
                              className="ts-icon-btn ts-icon-btn-danger"
                              onClick={() => handleDelete(entry.id)}
                              title="Delete"
                            >
                              ✕
                            </button>
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </div>

        {modalState && (
          <EditTimeEntryModal
            mode={modalState.mode}
            userId={selectedUserId}
            initial={modalState.initial}
            lockedDate={null}
            allowOpenShift={true}
            onClose={() => setModalState(null)}
            onSaved={handleModalSaved}
          />
        )}
      </div>
    </div>
  );
};

export default TimesheetPage;
